/**
 * 메모리 기반 캐시 시스템
 * HTTP 환경에서 사용할 수 있는 React 레벨 캐싱
 * 메모리 캐시 + IndexedDB + Web Worker 활용
 */

// 메모리 캐시 저장소
const memoryCacheStore = new Map();

// IndexedDB 설정
const DB_NAME = 'AIACS_Cache';
const DB_VERSION = 1;
const STORE_NAME = 'api_cache';

// Web Worker for background operations
let cacheWorker = null;

class MemoryCache {
  constructor() {
    this.initIndexedDB();
    this.initWebWorker();
    this.cleanupInterval = null;
    this.startCleanupTimer();
  }

  /**
   * IndexedDB 초기화
   */
  async initIndexedDB() {
    if (!('indexedDB' in window)) {
      console.warn('IndexedDB를 지원하지 않는 브라우저입니다.');
      return;
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => {
        console.error('IndexedDB 열기 실패');
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        console.log('IndexedDB 초기화 성공');
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          objectStore.createIndex('expiresAt', 'expiresAt', { unique: false });
          objectStore.createIndex('apiKey', 'apiKey', { unique: false });
        }
      };
    } catch (error) {
      console.error('IndexedDB 초기화 오류:', error);
    }
  }

  /**
   * Web Worker 초기화
   */
  initWebWorker() {
    if (typeof Worker === 'undefined') {
      console.warn('Web Worker를 지원하지 않는 브라우저입니다.');
      return;
    }

    // Inline Web Worker 생성
    const workerCode = `
      self.addEventListener('message', function(e) {
        const { type, data } = e.data;
        
        switch(type) {
          case 'cleanup':
            // 만료된 캐시 정리 로직
            const now = Date.now();
            const expiredKeys = data.filter(item => item.expiresAt < now);
            self.postMessage({ type: 'cleanup-result', data: expiredKeys });
            break;
            
          case 'compress':
            // 대용량 데이터 압축 (간단한 예시)
            try {
              const compressed = JSON.stringify(data);
              self.postMessage({ type: 'compress-result', data: compressed });
            } catch (error) {
              self.postMessage({ type: 'compress-error', error: error.message });
            }
            break;
        }
      });
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    
    try {
      cacheWorker = new Worker(workerUrl);
      
      cacheWorker.addEventListener('message', this.handleWorkerMessage.bind(this));
      cacheWorker.addEventListener('error', (error) => {
        console.error('Web Worker 오류:', error);
      });
    } catch (error) {
      console.error('Web Worker 생성 실패:', error);
    }
  }

  /**
   * Worker 메시지 처리
   */
  handleWorkerMessage(event) {
    const { type, data } = event.data;
    
    switch(type) {
      case 'cleanup-result':
        // 만료된 캐시 삭제
        data.forEach(item => {
          memoryCacheStore.delete(item.key);
          this.removeFromIndexedDB(item.key);
        });
        break;
    }
  }

  /**
   * 메모리와 IndexedDB에 데이터 저장
   */
  async set(key, data, options = {}) {
    const {
      expiryMinutes = 5,
      apiKey = 'default',
      useIndexedDB = true,
      priority = 'normal' // 'high', 'normal', 'low'
    } = options;

    const cacheData = {
      key,
      data,
      apiKey,
      timestamp: Date.now(),
      expiresAt: Date.now() + (expiryMinutes * 60 * 1000),
      priority,
      size: this.getDataSize(data)
    };

    // 1. 메모리 캐시에 즉시 저장
    memoryCacheStore.set(key, cacheData);

    // 2. 메모리 사용량 체크 및 정리
    this.checkMemoryUsage();

    // 3. IndexedDB에 비동기 저장 (대용량 데이터)
    if (useIndexedDB && this.db && cacheData.size > 1024) { // 1KB 이상
      await this.saveToIndexedDB(cacheData);
    }

    // 4. localStorage 동기화 (중요한 소량 데이터)
    if (priority === 'high' && cacheData.size < 100 * 1024) { // 100KB 미만
      try {
        localStorage.setItem(`mem_${key}`, JSON.stringify({
          expiresAt: cacheData.expiresAt,
          apiKey: cacheData.apiKey
        }));
      } catch (e) {
        // 용량 초과 시 무시
      }
    }

    console.log(`[MemoryCache] 저장: ${key}, 크기: ${(cacheData.size / 1024).toFixed(2)}KB`);
    return true;
  }

  /**
   * 데이터 가져오기 (계층적 검색)
   */
  async get(key) {
    // 1. 메모리 캐시에서 먼저 확인
    const memData = memoryCacheStore.get(key);
    if (memData && memData.expiresAt > Date.now()) {
      console.log(`[MemoryCache] 메모리 히트: ${key}`);
      return memData.data;
    }

    // 2. IndexedDB에서 확인
    if (this.db) {
      const idbData = await this.getFromIndexedDB(key);
      if (idbData && idbData.expiresAt > Date.now()) {
        // 메모리 캐시로 승격
        memoryCacheStore.set(key, idbData);
        console.log(`[MemoryCache] IndexedDB 히트: ${key}`);
        return idbData.data;
      }
    }

    // 3. localStorage 메타데이터 확인 (폴백)
    try {
      const lsMeta = localStorage.getItem(`mem_${key}`);
      if (lsMeta) {
        const meta = JSON.parse(lsMeta);
        if (meta.expiresAt > Date.now()) {
          // localStorage에서 실제 데이터 확인
          const lsData = localStorage.getItem(`api_cache_${meta.apiKey}_${key}`);
          if (lsData) {
            const parsed = JSON.parse(lsData);
            console.log(`[MemoryCache] localStorage 히트: ${key}`);
            return parsed.data;
          }
        }
      }
    } catch (e) {
      // 무시
    }

    return null;
  }

  /**
   * 캐시 삭제
   */
  async remove(key) {
    // 모든 계층에서 삭제
    memoryCacheStore.delete(key);
    await this.removeFromIndexedDB(key);
    
    try {
      localStorage.removeItem(`mem_${key}`);
    } catch (e) {
      // 무시
    }
  }

  /**
   * 전체 캐시 클리어
   */
  async clear() {
    // 메모리 캐시 클리어
    memoryCacheStore.clear();

    // IndexedDB 클리어
    if (this.db) {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      objectStore.clear();
    }

    // localStorage 관련 항목 클리어
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('mem_') || key.startsWith('api_cache_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // 무시
    }
  }

  /**
   * IndexedDB에 저장
   */
  async saveToIndexedDB(data) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * IndexedDB에서 가져오기
   */
  async getFromIndexedDB(key) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve(null);
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * IndexedDB에서 삭제
   */
  async removeFromIndexedDB(key) {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve();
        return;
      }

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 데이터 크기 계산
   */
  getDataSize(data) {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch (e) {
      return 0;
    }
  }

  /**
   * 메모리 사용량 체크 및 정리
   */
  checkMemoryUsage() {
    const MAX_MEMORY_ENTRIES = 1000;
    const MAX_MEMORY_SIZE = 50 * 1024 * 1024; // 50MB

    if (memoryCacheStore.size > MAX_MEMORY_ENTRIES) {
      // LRU 방식으로 오래된 항목 제거
      const entries = Array.from(memoryCacheStore.entries());
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = sorted.slice(0, Math.floor(MAX_MEMORY_ENTRIES * 0.2)); // 20% 제거

      toRemove.forEach(([key]) => {
        memoryCacheStore.delete(key);
      });

      console.log(`[MemoryCache] 메모리 정리: ${toRemove.length}개 항목 제거`);
    }
  }

  /**
   * 주기적 정리 시작
   */
  startCleanupTimer() {
    // 5분마다 만료된 캐시 정리
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000);
  }

  /**
   * 만료된 캐시 정리
   */
  async cleanup() {
    const now = Date.now();
    let removedCount = 0;

    // 메모리 캐시 정리
    for (const [key, value] of memoryCacheStore.entries()) {
      if (value.expiresAt < now) {
        memoryCacheStore.delete(key);
        removedCount++;
      }
    }

    // IndexedDB 정리 (Web Worker 활용)
    if (this.db && cacheWorker) {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const index = objectStore.index('expiresAt');
      const range = IDBKeyRange.upperBound(now);
      const request = index.getAllKeys(range);

      request.onsuccess = (event) => {
        const expiredKeys = event.target.result;
        if (expiredKeys.length > 0) {
          // Web Worker로 정리 작업 위임
          cacheWorker.postMessage({
            type: 'cleanup',
            data: expiredKeys.map(key => ({ key, expiresAt: 0 }))
          });
        }
      };
    }

    if (removedCount > 0) {
      console.log(`[MemoryCache] 정리 완료: ${removedCount}개 항목 제거`);
    }
  }

  /**
   * 캐시 상태 정보
   */
  async getStatus() {
    const status = {
      memory: {
        count: memoryCacheStore.size,
        entries: []
      },
      indexedDB: {
        available: !!this.db,
        count: 0,
        totalSize: 0
      },
      performance: {
        memoryUsage: 0
      }
    };

    // 메모리 캐시 정보
    let totalMemSize = 0;
    for (const [key, value] of memoryCacheStore.entries()) {
      totalMemSize += value.size || 0;
      status.memory.entries.push({
        key,
        size: value.size,
        expiresAt: new Date(value.expiresAt),
        priority: value.priority
      });
    }
    status.memory.totalSize = totalMemSize;

    // IndexedDB 정보
    if (this.db) {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const countRequest = objectStore.count();

      await new Promise((resolve) => {
        countRequest.onsuccess = () => {
          status.indexedDB.count = countRequest.result;
          resolve();
        };
      });
    }

    // 메모리 사용량 (지원하는 경우)
    if (performance.memory) {
      status.performance.memoryUsage = performance.memory.usedJSHeapSize;
    }

    return status;
  }

  /**
   * 프리페치 - 미리 데이터 가져오기
   */
  async prefetch(urls, options = {}) {
    const promises = urls.map(url => {
      return fetch(url)
        .then(response => response.json())
        .then(data => {
          const key = `prefetch_${url}`;
          return this.set(key, data, { ...options, priority: 'low' });
        })
        .catch(error => {
          console.error(`[MemoryCache] 프리페치 실패: ${url}`, error);
        });
    });

    return Promise.allSettled(promises);
  }
}

// 싱글톤 인스턴스
const memoryCache = new MemoryCache();

export default memoryCache; 